import { authTokenCache } from '@/lib/auth-token';
import type { CsvContactRow } from './csv-import';
import type { ThroxyImportContact } from './throxy-csv';

// =====================================================================
// Operator-console REST client — the SPA's first plain-HTTP path to the BFF
// (the cockpit uses only the WebSocket). Every call hits `/bff/admin/*`
// SAME-ORIGIN: prod via CloudFront, dev via the Vite proxy (vite.config.ts
// forwards `/bff` → localhost:8080). The BFF verifies the bearer JWT and proxies
// to the core's in-VPC `/dialer/admin/*` (HermesWeb.AdminController).
//
// The JWT rides as `Authorization: Bearer <token>` — the REST analogue of the
// cockpit WS's `?token=`. Locally the BFF runs with auth OFF and accepts requests
// anonymously, so a missing token is fine in dev (authClient.token() just yields
// none); in prod the signed-in @throxy.com session supplies it.
// =====================================================================

const ADMIN_BASE = '/bff/admin';

// The shape the core/BFF return for a failed call (422 validation, 404, etc.).
// `details` is the per-field changeset errors the board surfaces inline.
export interface AdminError {
  error: string;
  details?: Record<string, string[]>;
}

// Thrown on a non-2xx response so callers can `try/catch` and show the message.
// Carries the HTTP status + the parsed problem body (when JSON).
export class AdminRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: AdminError | string,
  ) {
    super(typeof body === 'string' ? body : describeError(body));
    this.name = 'AdminRequestError';
  }
}

function describeError(body: AdminError): string {
  if (body.details) {
    const parts = Object.entries(body.details).map(([f, msgs]) => `${f}: ${msgs.join(', ')}`);
    if (parts.length) return `${body.error} (${parts.join('; ')})`;
  }
  return body.error;
}

// Best-effort bearer token, cached + coalesced (see token-cache.ts for why: the
// board fires ~6 admin fetches in parallel, and minting per-request rate-limits
// the BFF's /bff/auth/token (429) → tokenless calls → 401). Shared with the cockpit
// WS token so a 401 here also drops the copy the sockets present. Locally (auth off)
// token() yields none and we omit the header (the BFF allows anonymous in dev).
async function authHeader(): Promise<Record<string, string>> {
  const token = await authTokenCache.get();
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const headers: Record<string, string> = { ...(await authHeader()) };
  if (body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(`${ADMIN_BASE}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // A 401 likely means our cached token expired — drop it and retry ONCE with a
  // fresh mint (only if we actually sent one; tokenless dev never loops).
  if (res.status === 401 && retry && headers.authorization) {
    authTokenCache.invalidate();
    return request<T>(method, path, body, false);
  }

  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    throw new AdminRequestError(
      res.status,
      isAdminError(parsed) ? parsed : text || `request failed (${res.status})`,
    );
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isAdminError(v: unknown): v is AdminError {
  return typeof v === 'object' && v !== null && typeof (v as AdminError).error === 'string';
}

export const adminApi = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  del: <T>(path: string): Promise<T> => request<T>('DELETE', path),
};

// =====================================================================
// Entity row shapes (mirror HermesWeb.AdminController's JSON). Ids are integers
// from the core; the board stringifies them where it needs map keys.
// =====================================================================

export interface AdminClient {
  id: number;
  name: string;
  // The shared cross-service Throxy id — leadflow's match key. Null until set on
  // create; only id-bearing clients are offered as import targets.
  throxy_client_id: string | null;
}
export interface AdminPool {
  id: number;
  name: string;
  color: string | null;
  hidden: boolean;
  // Joinable by every SDR, not only the ones assigned to it.
  open_to_all: boolean;
  // Lifecycle state: draft | open | calling | paused | draining | closed. Gates SDR
  // joins (open/calling/paused are joinable; `calling` = SDRs actively dialing).
  // Shown as a badge on the pool column.
  run_state: string;
  // Leads dialable at this instant, counting the ones a redial pass would unclaim.
  // Absent on older cores.
  dialable_including_claimed?: number;
  // Dialing config (HH:MM:SS from the core; max_attempts null = unlimited).
  callable_start: string | null;
  callable_end: string | null;
  max_attempts: number | null;
  retry_dial_seconds: number;
  // One-pass mode: dial each contact once (in list order), then stop.
  sequential: boolean;
  // Predictive dialing: how many calls each SDR dials in parallel (1 = serial).
  calls_per_sdr: number;
  // How many "Redial no-pickups" passes this pool has been through (0 = never).
  // Bumped by the core inside the redial transaction, so it is a record of what
  // happened, not a setting — there is no way to edit it from the console. Drives
  // the `redial` badge that distinguishes a reopened second pass from a fresh pool.
  redial_count: number;
  // Double-pickup fallback: a sibling a human also answered is transferred here
  // (E.164) instead of being hung up. null = transfer disabled.
  redirect_number: string | null;
  // Operator pause latch (distinct from the auto `paused` run_state a pool with no
  // SDRs shows). When true an operator has explicitly halted new dials; run_state
  // is `paused` and stays there until an operator resumes. Drives the pause/resume
  // toggle on the pool column.
  operator_paused: boolean;
  // Email of the @throxy.com operator who created this pool (create/import/clone),
  // forwarded by the BFF from the signed-in session. null for pools created before
  // this was tracked, or outside an authenticated session (local/CI). Shown at the
  // foot of the pool column.
  created_by: string | null;
  // Pool creation time (ISO-8601 UTC, the row's inserted_at). Always present;
  // rendered next to created_by in the card footer.
  created_at: string;
  // Contacts done — dialed at least once (Postgres `pool_contacts.dialed_at`, so a
  // merged pool's carried progress is counted; a ClickHouse-by-pool_id scan would
  // miss it). Paired with `contact_count` to show "X of Y called".
  called_contacts: number;
  // Contacts still to dial (Postgres `dialed_at IS NULL`) — the same number the
  // cockpit shows, and `contact_count - called_contacts`.
  remaining_count: number;
  // Total contacts linked to this pool (pool_contacts membership count). Lets the
  // console show the count + gate draft→Open without loading the contacts table.
  contact_count: number;
  // LLM spend on this pool's AI call dispositions, in micro-USD (millionths of a
  // dollar; from ClickHouse). 0 when the pool has no dispositioned calls. Priced from
  // the model's token usage at list rates; only accrues forward from when token
  // capture shipped. Rendered as a dollar badge on the pool card. Scoped to calls
  // placed UNDER this pool's id — for a merge survivor that's its own era, not the
  // folded sources' (whose spend stays on their frozen cards). null when it could not be
  // read — distinct from 0, which is a figure we have.
  llm_spend_micros: number | null;
  // Estimated Twilio telephony spend on this pool's calls, in micro-USD (from
  // ClickHouse). 0 when the pool has no completed calls. Estimated from each call's
  // duration × Twilio list rates (per destination country + caller-ID origination);
  // only accrues forward from when capture shipped. Same this-pool-era scope as
  // `llm_spend_micros`. Rendered as a dollar badge; null like it when unread.
  twilio_spend_micros: number | null;
  // Human pickups: contacts that reached a real person (AMD-ruled voicemails don't
  // count; from ClickHouse pool_events) — always the same number as the outcomes
  // popover's Humans bucket. Numerator of the card's pickup rate. Scoped to calls
  // placed under this pool's id (this-pool-era, like the spends). null when unread.
  human_pickups: number | null;
  // Dials (initiated call events, from ClickHouse) placed under this pool's id.
  // Denominator of the card's pickup rate; the stat is hidden while 0, and marked
  // unknown when null.
  total_dials: number | null;
  /**
   * Present ONLY on the list_pools response (the temporary "Fix pool clients"
   * tool). A pool has no client column — this is the majority client across its
   * contacts. null when the pool is empty. throxy_client_id null → the pool is
   * filed under an id-less client and needs reassigning.
   *
   * @deprecated Temporary. Remove with the "Fix pool clients" tool once pools are
   * natively multi-client — a pool won't have a single "effective client" then.
   */
  effective_client?: EffectiveClient | null;
  // Present on the list_pools response: the pool's full client mix derived from
  // its contacts (a pool can span several clients), most leads first. Drives the
  // card's client chips. Empty for a pool with no contacts.
  //
  // Three different populations, and the chip needs all three. `count` is every member,
  // which is what a drag moves. `available_now` is what could be dialed at this instant
  // — past every wait AND inside its calling window — so it is the only one that answers
  // "is there work here". `total_available` is every member not permanently blocked, the
  // denominator the availability panel uses.
  clients?: {
    id: number;
    name: string | null;
    count: number;
    undialed: number;
    available_now?: number;
    total_available?: number;
  }[];
}

/**
 * The majority client of a pool's contacts, for the "Fix pool clients" tool.
 *
 * @deprecated Temporary. Introduced only to flag/repair pools filed under a
 * client with no throxy_client_id; delete once pools are natively multi-client.
 */
export interface EffectiveClient {
  id: number;
  name: string;
  throxy_client_id: string | null;
  // Contacts under this client (the count that won the majority).
  contact_count: number;
}

// Partial dialing-config patch — only the keys present are changed (PATCH
// /pools/:id). Times are HH:MM or HH:MM:SS.
export interface PoolConfigPatch {
  callable_start?: string;
  callable_end?: string;
  retry_dial_seconds?: number;
  sequential?: boolean;
  calls_per_sdr?: number;
  // E.164 number, or '' to clear (the core normalizes blank → null).
  redirect_number?: string;
  // Lifecycle state: draft | open | calling | paused | draining | closed.
  run_state?: string;
  // Operator pause: true halts new dials (keeps SDRs + in-flight calls) and pins
  // run_state to `paused`; false resumes. The core makes it take effect on the
  // live dialer and un-sticks a pool stranded `calling` by a deploy.
  operator_paused?: boolean;
}
export interface AdminSdr {
  id: number;
  name: string;
  // The SDR's login email — the identity the cockpit resolves them by, so an
  // assignment lines up with the signed-in person. Null for dev/legacy rows.
  email: string | null;
  // An SDR can be assigned to MANY pools now (the pool_sdrs m2m, mirroring
  // contacts). `pool_ids` is the flat membership set; empty = unassigned. The
  // assignment scopes which pools the SDR sees in the cockpit "Choose a pool".
  pool_ids: number[];
}
export interface AdminPhoneNumber {
  id: number;
  number: string;
  // No pool membership: a number is global inventory that EVERY pool dials from.
  status: string;
  // The effective per-turn call cap (calls a number makes before rotation moves on).
  // Already reflects an operator override.
  turn_cap: number;
  // The raw operator override for the per-turn cap: null = automatic (age-based
  // warm-up ramp), a number = a manually-set cap. Lets the board badge custom vs auto.
  turn_cap_override: number | null;
}

// pool_id (string) -> [live sdr id]: who's calling now per the core runtime.
export type ActiveSdrs = Record<string, number[]>;

// The assignable people, sourced from the AUTH PROVIDER (the real @throxy.com
// Google sign-ins in Better Auth's `user` table, served by GET /bff/sdr-directory)
// — NOT the hand-maintained core `sdrs` table. This is the list the "Assign SDRs"
// modal shows; assignment is keyed by `email` (the same identity the cockpit
// resolves at login). Empty when auth is off (local/CI) → the board falls back to
// the core SDRs so dev still works.
export interface SdrDirectoryEntry {
  email: string;
  name: string;
  // Google profile photo from the auth table; optional — older BFFs and the
  // auth-off dev fallback omit it, and every consumer renders without it.
  image?: string | null;
}

// One snapshot of everything the board paints. Fetched on open, on Refresh, and
// after each successful mutation (the original console's renderBoard() cadence).
export interface BoardData {
  clients: AdminClient[];
  pools: AdminPool[];
  // The core SDR rows (numeric ids) — used to resolve "calling now" runtime ids to
  // names and to read each person's pool assignments (by email). The assignable
  // roster the UI shows is `directory` merged with these (see buildSdrRoster).
  sdrs: AdminSdr[];
  directory: SdrDirectoryEntry[];
  numbers: AdminPhoneNumber[];
  active: ActiveSdrs;
}

// Global spend for one window (micro-USD) plus call and pool counts, mirroring
// HermesWeb.AdminController's global_spend JSON. The spend figures are lifetime-to-date
// within the window; the combined total is summed in the UI, not on the wire.
// call_count is the number of distinct calls placed in the window; pool_count is the
// number of pools created in the window.
export interface SpendWindow {
  twilio_micros: number;
  llm_micros: number;
  call_count: number;
  pool_count: number;
}

// Global Twilio + LLM spend for today, the current week and the current month (UTC
// boundaries).
export interface GlobalSpend {
  today: SpendWindow;
  week: SpendWindow;
  month: SpendWindow;
}

// Total Twilio + LLM spend for today / the current week / month, across all pools. Its
// own call (not folded into fetchBoard) so the cost widget can refresh independently of
// the board poll. GET /bff/admin/spend → core /dialer/admin/spend.
export function fetchGlobalSpend(): Promise<GlobalSpend> {
  return adminApi.get<GlobalSpend>('/spend');
}

export async function fetchBoard(): Promise<BoardData> {
  // Deliberately NOT fetching the global /contacts list — it's the whole contacts
  // table (~10 MB) and this runs every poll.
  const [clients, pools, sdrs, directory, numbers, active] = await Promise.all([
    adminApi.get<{ clients: AdminClient[] }>('/clients'),
    adminApi.get<{ pools: AdminPool[] }>('/pools'),
    adminApi.get<{ sdrs: AdminSdr[] }>('/sdrs'),
    fetchSdrDirectory(),
    adminApi.get<{ phone_numbers: AdminPhoneNumber[] }>('/phone-numbers'),
    adminApi.get<{ active: ActiveSdrs }>('/active-sdrs'),
  ]);
  return {
    clients: clients.clients ?? [],
    pools: pools.pools ?? [],
    sdrs: sdrs.sdrs ?? [],
    directory,
    numbers: numbers.phone_numbers ?? [],
    active: active.active ?? {},
  };
}

// The SDR directory lives at /bff/sdr-directory (NOT under /bff/admin, so it isn't
// proxied to the core) — the BFF reads it straight from the Better Auth user store.
// Degrades to [] on any failure so a directory hiccup never blanks the whole board.
export async function fetchSdrDirectory(): Promise<SdrDirectoryEntry[]> {
  try {
    const headers = await authHeader();
    const res = await fetch('/bff/sdr-directory', { headers });
    if (!res.ok) return [];
    const data = (await res.json()) as { sdrs?: SdrDirectoryEntry[] };
    return data.sdrs ?? [];
  } catch {
    return [];
  }
}

// One contact's full detail (GET /contacts/:id) — the timeline contact modal's
// payload. `contact` mirrors the cockpit card (metadata-derived company/title/…).
export interface AdminContactDetail {
  id: number;
  contact: {
    name: string;
    phone: string;
    timezone: string | null;
    email: string | null;
    company: string | null;
    title: string | null;
    persona: string | null;
    linkedin_url: string | null;
    location: string | null;
  };
  tags: string[];
  dnc_at: string | null;
  client: { name: string | null; throxy_client_id: string | null } | null;
}

export const fetchContactDetail = (id: number): Promise<AdminContactDetail> =>
  adminApi.get<AdminContactDetail>(`/contacts/${id}`);

// Leadflow lead id for (client, phone) via the BFF (which holds the leadflow key
// server-side). null = the contact isn't a lead in leadflow — the normal case —
// or the lookup is unavailable; either way the modal shows "not a lead yet".
export async function fetchLeadflowLeadId(clientId: string, phone: string): Promise<string | null> {
  try {
    const qs = new URLSearchParams({ clientId, phone });
    const res = await fetch(`/bff/leadflow/lead?${qs}`, { headers: await authHeader() });
    if (!res.ok) return null;
    const data = (await res.json()) as { leadId?: unknown };
    return typeof data.leadId === 'string' && data.leadId !== '' ? data.leadId : null;
  } catch {
    return null;
  }
}

// --- Mutations (each resolves to the fresh row; the board re-fetches after) ---

// Create a client with the two fields that make it leadflow-syncable. The core
// accepts an id-less client too (the hidden plain-CSV path), but the console form
// requires the Throxy id so imported leads always resolve downstream.
export const createClient = (displayName: string, throxyClientId: string): Promise<unknown> =>
  adminApi.post('/clients', { name: displayName, throxy_client_id: throxyClientId });

// Name-only create: the pool lands empty (`draft`, no clients/leads) — the
// modal's no-CSV path; lists arrive later and are moved in via "Manage leads".
export const createPool = (name: string): Promise<unknown> => adminApi.post('/pools', { name });

// Clone a pool: the core copies its settings, contacts (with dial-order) and
// caller-ID links into a new pool named "<source> (copy N)". SDRs aren't copied.
export const clonePool = (id: number): Promise<unknown> => adminApi.post(`/pools/${id}/clone`);

export const deletePool = (id: number): Promise<unknown> => adminApi.del(`/pools/${id}`);

export const updatePool = (id: number, patch: PoolConfigPatch): Promise<unknown> =>
  adminApi.patch(`/pools/${id}`, patch);

// Rename is its own endpoint rather than a PoolConfigPatch field: it's the only
// path where the core validates the name (>= 4 chars, no clash with another pool),
// so a 422 here is a field error the caller should render against the input.
export const renamePool = (id: number, name: string): Promise<unknown> =>
  adminApi.patch(`/pools/${id}/name`, { name });

/**
 * Temporary admin tool: move every contact in the pool onto `clientId` (rewrites
 * contacts.client_id — pools have no client column). Contacts whose phone already
 * exists under the target client are skipped; the response reports how many moved
 * vs skipped. Target must be a client with a throxy_client_id.
 *
 * @deprecated Temporary. Remove with the "Fix pool clients" tool once pools are
 * natively multi-client (overwriting "the pool's client" stops making sense then).
 */
export const reassignPoolClient = (
  poolId: number,
  clientId: number,
): Promise<{ ok: boolean; moved: number; skipped: number }> =>
  adminApi.post(`/pools/${poolId}/reassign-client`, { client_id: clientId });

// =====================================================================
// Global AMD (answering-machine-detection) tuning — a singleton, live-editable
// from the console. Mirrors HermesWeb.AdminController's /dialer/admin/settings
// JSON. The core serves these from an in-memory cache and refreshes every node
// on save, so an edit takes effect on the next dial fleet-wide. Twilio bounds:
// SpeechThreshold 1000-6000, SpeechEndThreshold 500-5000.
// =====================================================================
export interface AmdSettings {
  // MachineDetection=Enable+AsyncAmd (true) vs synchronous DetectMessageEnd
  // (false). LOAD-BEARING rollback lever — see the console panel's warning.
  amd_async: boolean;
  // ms of speech Twilio uses to classify human-vs-machine (1000-6000).
  amd_speech_threshold_ms: number;
  // ms of silence after speech before Twilio finalizes the verdict (500-5000).
  amd_speech_end_threshold_ms: number;
  // The two below are optional ONLY for the length of a deploy: deploy.yml publishes
  // the SPA before rolling dialer-core, so for a few minutes this panel talks to a core
  // whose /settings response predates them. Absent means "this core cannot tune it
  // yet" — a state the panel must tell apart from a value, because substituting one
  // would write the substitute over the operator's real row on the next save.
  //
  // ms to wait for Twilio's verdict after an unconfirmed bridge before assuming
  // human and showing the card anyway (500-30000 — our bound, not Twilio's).
  amd_card_fallback_ms?: number;
  // Whether a machine verdict landing after the SDR is unmuted is ignored and the call
  // kept live (true) or allowed to hang up (false). Paired with the fallback above.
  late_machine_protect_unmuted?: boolean;
  // Optional for the same deploy-window reason. Seconds the lead's leg keeps recording
  // after a machine verdict before it is hung up; 0 hangs up at once. The SDR is freed
  // at the verdict either way.
  greeting_record_s?: number;
  // Optional for the same deploy-window reason as the two above. Whether an answered call
  // that found every SDR busy is held briefly instead of hung up on, for how long, and how
  // many a pool may carry per SDR.
  hold_unrouted?: boolean;
  hold_deadline_ms?: number;
  hold_per_sdr?: number;
  // Optional for the same deploy-window reason, and separately NULLABLE: `null` is off,
  // `undefined` is a core too old to tune it.
  overdial_pacing_ms?: number | null;
  eastern_open_hour?: number;
  // The cockpit mutual-silence gate (plans/silence-cutoff/plan.md), optional for the
  // same deploy-window reason. Delivered to the cockpit on every card push, so an
  // edit lands on the next call. The mode ladder is off → shadow (receipts only) →
  // prompt (banner) → cut (banner + countdown + hangup).
  silence_gate_mode?: string;
  silence_floor_ms?: number;
  silence_quiet_ms?: number;
  // The lead-only tier: lead silence alone, regardless of SDR audio.
  silence_lead_quiet_ms?: number;
  silence_grace_ms?: number;
  // Audibility thresholds on the SDK's 0–1 volume levels (floats, 0–1 exclusive).
  silence_remote_max?: number;
  silence_local_min?: number;
  // The modes this core accepts, for the select — server-owned like the bounds.
  silence_gate_modes?: string[];
}

// Partial patch — only the keys present are changed (PATCH /settings).
// `note` is write-only: it is not part of the settings row, it labels the resulting
// row in the append-only `amd_settings_log` history so a tuning experiment's arms can
// be grouped by name instead of by hand-maintained time ranges.
export type AmdSettingsPatch = Partial<AmdSettings> & { note?: string };

export const getAmdSettings = (): Promise<AmdSettings> =>
  adminApi.get<{ settings: AmdSettings }>('/settings').then((r) => r.settings);

export const updateAmdSettings = (patch: AmdSettingsPatch): Promise<AmdSettings> =>
  adminApi.patch<{ ok: true; settings: AmdSettings }>('/settings', patch).then((r) => r.settings);

// The gatekeeper audio fork (plans/gatekeeper): a copy of the lead's first seconds
// goes to the classifier service while the call bridges as normal. `enabled` is the
// kill switch and `sample_pct` the ramp dial; BOTH must be open for a call to fork,
// and the core also needs a stream URL it only reads at boot.
//
// `act_voicemail_pct` is the other dial, and the only one that changes a call: the share
// of forked calls on which a voicemail verdict the classifier is sure of ENDS the call,
// the way a Twilio machine verdict does. 0 ends nothing. `act_ai_gatekeeper_pct` is its
// twin for AI call screeners: a column that stays 0 and that NOTHING can edit -- not this
// panel, not the API -- until it is decided whether a screener should be cut at all, since
// a person can pick up behind one. It shows in `effective` only because the core's cache
// carries it.
//
// The other five fields the row carries (deadline_ms, pass_schedule_ms, limit_words,
// min_words_to_classify, full_deadline_sample_pct) are DELIBERATELY not editable here:
// nothing reads them yet — the service runs on its own values — so editing one would
// write a history row for an arm that never took effect. They come back when the core
// signs them onto each stream. See Q5 in plans/gatekeeper/plan.md.
export interface GatekeeperSettings {
  enabled: boolean;
  sample_pct: number;
  // Optional for the same deploy-window reason as the fields below: a core that predates
  // the column does not send it, and would 422 a PATCH that named it -- so the panel
  // offers the control only when the core says it has one.
  act_voicemail_pct?: number;
  // Present but not editable from here, as above. Optional for the length of a deploy:
  // the SPA publishes before dialer-core rolls, so this panel can briefly talk to a core
  // whose response predates a field.
  deadline_ms?: number;
  pass_schedule_ms?: string;
  limit_words?: number;
  min_words_to_classify?: number;
  full_deadline_sample_pct?: number;
  updated_at?: string;
}

// What the fleet is actually applying, from the core's own config cache — not the row.
// A node that has not picked the broadcast up yet shows here, which is the difference
// between "saved" and "live".
export interface GatekeeperEffective {
  enabled: boolean;
  sample_pct: number;
  act_voicemail_pct?: number;
  act_ai_gatekeeper_pct?: number;
}

export interface GatekeeperSettingsResponse {
  settings: GatekeeperSettings;
  effective: GatekeeperEffective;
}

// `note` is write-only: it labels the row this edit appends to the gatekeeper history,
// so a shadow-mode arm can be found by name rather than by remembering a time range.
export type GatekeeperSettingsPatch = {
  enabled?: boolean;
  sample_pct?: number;
  act_voicemail_pct?: number;
  note?: string;
};

export const getGatekeeperSettings = (): Promise<GatekeeperSettingsResponse> =>
  adminApi.get<GatekeeperSettingsResponse>('/gatekeeper-settings');

export const updateGatekeeperSettings = (
  patch: GatekeeperSettingsPatch,
): Promise<GatekeeperSettingsResponse> =>
  adminApi
    .patch<{ ok: true } & GatekeeperSettingsResponse>('/gatekeeper-settings', patch)
    .then((r) => ({ settings: r.settings, effective: r.effective }));

// The inbound-call switch (plans/inbound-calls/plan.md): what happens when a
// prospect rings one of our caller IDs back. `off` rejects and records nothing
// (the panic brake), `shadow` rejects but resolves + logs who called, `live`
// bridges the caller to a free SDR. Flipping it is a settings write — no deploy.
export type InboundCallMode = 'off' | 'shadow' | 'live';

export interface InboundCallSettings {
  mode: InboundCallMode;
  updated_at: string;
}

// `effective_mode` is what the serving node's cache is ACTUALLY dispatching —
// normally equal to settings.mode; surfaced so a row/cache divergence is visible
// rather than guessed at. `modes` is the server's own vocabulary, so the switch
// renders what THIS core can do (an old build refuses `live` with a 422).
export interface InboundCallSettingsResponse {
  settings: InboundCallSettings;
  effective_mode: InboundCallMode;
  modes: InboundCallMode[];
}

export const getInboundCallSettings = (): Promise<InboundCallSettingsResponse> =>
  adminApi.get<InboundCallSettingsResponse>('/inbound-call-settings');

export const updateInboundCallMode = (
  mode: InboundCallMode,
): Promise<{ settings: InboundCallSettings; effective_mode: InboundCallMode }> =>
  adminApi.patch<{ ok: true; settings: InboundCallSettings; effective_mode: InboundCallMode }>(
    '/inbound-call-settings',
    { mode },
  );

// One row of the per-pool timeline (newest first). event_type is the taxonomy
// (ringing, answered, voicemail_detected, sdr_joined, retry_scheduled, …);
// call/contact/sdr ids are null for non-call events; `at` is ISO-8601. `seq` is a
// per-emission tiebreaker; together (at, seq) form the keyset cursor for paging.
// contact_name/throxy_client_id resolve the lead's name and leadflow link;
// from_number/to_number are the caller ID and dialed number, null on non-call rows.
export interface PoolEvent {
  event_type: string;
  sdr_id: number | null;
  call_id: string | null;
  contact_id: number | null;
  contact_name: string | null;
  throxy_client_id: string | null;
  from_number: string | null;
  to_number: string | null;
  detail: string | null;
  at: string;
  seq: number;
}

// Keyset cursor for infinite scroll: the (at, seq) of the last (oldest) row shown.
export interface PoolEventsCursor {
  at: string;
  seq: number;
}

// One page of the timeline, newest first. `limit` caps the page (core clamps to
// 1..1000, default 200); `before` keyset-pages to the next, older batch without
// loading the whole timeline. A page shorter than `limit` means no more rows.
// `q` is an optional lead name/number substring, applied SERVER-SIDE against the
// FULL timeline (not just loaded pages) — appended only when non-empty.
export const fetchPoolEvents = (
  id: number,
  opts: { limit?: number; before?: PoolEventsCursor; q?: string } = {},
): Promise<PoolEvent[]> => {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.before) {
    qs.set('before_at', opts.before.at);
    qs.set('before_seq', String(opts.before.seq));
  }
  const q = opts.q?.trim();
  if (q) qs.set('q', q);
  const suffix = qs.size ? `?${qs}` : '';
  return adminApi
    .get<{ events: PoolEvent[] }>(`/pools/${id}/events${suffix}`)
    .then((r) => r.events ?? []);
};

// Full call-outcome breakdown for a pool (GET /pools/:id/outcomes). Every one of the
// pool's contacts lands in exactly one bucket, so the nine counts sum to `total` (the
// pool's contact count — the "Y" in "X of Y called"). `humans`/`voicemails`/`manual_drops`
// reached someone/something — `voicemails` is AMD-detected machines, `manual_drops` is
// machines the SDR flagged after AMD missed (together the old combined voicemail count;
// a voicemail that briefly flashed a card under async AMD counts once, counted by
// distinct CONTACT); `abandoned`/`busy`/`no_answer`/`failed` were dialed but
// never connected; `not_yet_called` were never dialed (Postgres, so it matches the
// card's "N to dial" — the Overview doesn't render it as a bucket, it only shrinks the
// denominator to contacts dialled); `carried` were dialed before this pool was merged,
// so their specific outcome sits under the source pool and isn't visible here (0
// unless the pool is a merge survivor). Fetched on demand when the outcomes popover
// opens (like fetchPoolEvents), not on every board poll.
export interface PoolOutcomes {
  humans: number;
  voicemails: number;
  manual_drops: number;
  abandoned: number;
  busy: number;
  no_answer: number;
  failed: number;
  not_yet_called: number;
  carried: number;
  total: number;
}

export const fetchPoolOutcomes = (id: number): Promise<PoolOutcomes> =>
  adminApi.get<PoolOutcomes>(`/pools/${id}/outcomes`);

// What a pool can dial, and when the rest comes back (GET /pools/:id/availability).
//
// `available_on_redial` is reported apart from `available_now`, not added in: those leads
// are claimed, so nothing dials them until a pass releases the claim. A number that stays
// high there is the automation having stopped.
//
// `bands` are exclusive and diagnostic — leads collapse to their group's soonest return,
// so a band is where a group lands, not where every lead in it does. It says when work
// RETURNS, not why a dial did not happen a second ago.
export type PoolAvailabilityBlocker =
  | 'off_hours'
  // The gap before we ring the same person again — hours to a week.
  | 'lead_cooldown'
  // This pool's own gap before it rings the same contact again — seconds, where the
  // lead's ladder above is counted across every pool and runs to a week.
  | 'pool_cooldown'
  // The provider will not place a call to this destination from any of our numbers, so
  // the lead rests until we try again. The only one of these an operator can act on: it
  // says the batch was mis-sourced, not that the dialer is busy.
  | 'provider_refused'
  // No calling window resolves for their timezone, so neither wait above is what holds
  // them.
  | 'unknown_timezone'
  | 'claimed_pending_redial'
  | 'none';

export interface PoolAvailabilityBand {
  band:
    | 'now'
    | 'within_1h'
    | 'within_3h'
    | 'within_1d'
    | 'within_1w'
    | 'beyond_1w'
    | 'unschedulable';
  blocker: PoolAvailabilityBlocker;
  count: number;
}

export interface PoolAvailabilityClient {
  client_id: number | null;
  available_now: number;
  available_on_redial: number;
  total_available: number;
}

// One line of the panel's timeline: a population and when it can next be rung.
//
// `at` is an instant when a shut calling window is the wait (`span: false`) — a zone
// turns over all at once, so the panel names the time and the zones. Otherwise it is the
// far edge of a band (`span: true`): rungs lapse lead by lead, so "by 15:00" is the only
// honest reading. `null` is ready now, past a week, or a zone with no window at all.
export interface PoolAvailabilityReturn {
  band: PoolAvailabilityBand['band'];
  blocker: PoolAvailabilityBlocker;
  at: string | null;
  span: boolean;
  zones: string[];
  count: number;
  // How many of `count` are already claimed by a pass. A window opening does not free
  // those on its own — a pass has to hand the claim back, which is automatic only while
  // auto-requeue is on.
  claimed: number;
}

// Leads by how often their handset has already rung, capped at the release cap — so the
// last bucket is "at or past the cap", the ones a release leaves alone.
export interface PoolRingDepth {
  depth: number;
  leads: number;
  rung_last_hour: number;
  resting: number;
}

// What pressing Release would actually do. `movable` is the promise — how many rungs move,
// matching the release's own predicate clause for clause. The split goes only as far as
// this data supports: whether the lead's calling window is open across the whole landing
// range. It does NOT say when a lead dials, because a released lead still waits out its
// floor and, if claimed, waits for a pass to hand the claim back.
export interface PoolReleasePreview {
  movable: number;
  window_open: number;
  awaiting_window: number;
  capped: number;
}

// The bounds that applied, echoed rather than hardcoded: every one of them is editable
// without a deploy, so a panel that names them has to name the ones in force.
export interface PoolAvailabilityPolicy {
  ring_ladder: number[];
  max_rings: number;
  floor_seconds: number;
  cooldown_enabled: boolean;
  auto_redial_enabled: boolean;
}

export interface PoolAvailability {
  available_now: number;
  available_on_redial: number;
  total_available: number;
  never: number;
  never_by_reason: Record<string, number>;
  bands: PoolAvailabilityBand[];
  by_client: PoolAvailabilityClient[];
  // Optional through the roll: a core that has not picked up this deploy answers without
  // them, and the panel hides those sections rather than rendering zeros.
  returns?: PoolAvailabilityReturn[];
  ring_depth?: PoolRingDepth[] | null;
  rung_last_hour?: number | null;
  release?: PoolReleasePreview | null;
  policy?: PoolAvailabilityPolicy;
}

export const fetchPoolAvailability = (id: number): Promise<PoolAvailability> =>
  adminApi.get<PoolAvailability>(`/pools/${id}/availability`);

// One-shot: it moves the rungs parked at the moment of the press and leaves nothing
// switched on. The two bounds come back with the count because both are editable without
// a deploy — a caller that states them has to read the ones that actually applied.
export interface PoolRungsReleaseResult {
  released: number;
  floor_seconds: number;
  max_rings: number;
}

export const releaseRungs = (id: number): Promise<PoolRungsReleaseResult> =>
  adminApi.post<PoolRungsReleaseResult>(`/pools/${id}/release-rungs`);

// =====================================================================
// Moving leads (POST /pools/:id/leads/move)
//
// One operator verb over one selection: where the chosen memberships go. Reached from
// the board — drag a client chip onto another pool. Everything is scoped to ONE
// client: a pool-wide action would hide whose leads it was spending. (The core also
// serves preview/remove and a per-pool leads summary; their console surface, the
// Leads tab, was removed — superseded by Availability and the automatic requeue.)
// =====================================================================

// What to act on. The selection mirrors the core's contract, which is wider than the
// board's drag-move needs — the board always sends `kind: 'both'`, a `stage` slice and
// `count: 'all'`. `order: 'tail'` keeps the source dialing its next-up leads and moves
// the overflow.
export interface LeadSelection {
  client_id: number;
  // Lead-scoped: how many times this lead has been rung under ANY pool.
  kind: 'fresh' | 'redialed' | 'both';
  // Pool-scoped and independent of `kind`: has THIS pool rung it yet. Absent means
  // `to_dial`. `dialled_here` reaches leads this pool has already rung — moving one is a
  // redial performed by the destination, so the core runs the redial gate over it and
  // can move fewer than the client's raw count.
  // `any` is both halves — the client's whole presence in the pool. Not one selection:
  // the core resolves and applies them separately because they land differently.
  stage?: 'to_dial' | 'dialled_here' | 'any';
  dials?: number;
  count?: number | 'all';
  order?: 'head' | 'tail';
}

export interface LeadMoveResult {
  moved: number;
  already_in_target: number;
  remaining_unclaimed: number;
}

export const moveLeads = (
  id: number,
  selection: LeadSelection,
  targetPoolId: number,
): Promise<LeadMoveResult> =>
  adminApi.post<LeadMoveResult>(`/pools/${id}/leads/move`, {
    ...selection,
    target_pool_id: targetPoolId,
  });

// How one caller ID is performing over the window (GET /phone-numbers/performance).
//
// `enough_data` is the contract, NOT a hint: below `min_sample` dials the rates are
// noise and MUST render as "not enough data yet". A 0% answer rate from 3 dials looks
// like a dead number and is nothing at all — showing it as a red 0% is how an operator
// releases good inventory. Rates are `null` (never 0) when there is nothing to divide,
// so `0` always means a real, measured zero.
//
// Attribution only exists for calls placed after `call_events.phone_id` was wired, so
// the window is "since we started recording", not all history.
// Probation verdict for a recently-bought number. `failing` is a RECOMMENDATION to
// review, never an action: releasing is irreversible, and the evidence (one market's
// answer rate over a small sample) isn't strong enough to spend that automatically.
// `probation` means "no verdict yet" — not "bad".
export type ProbationVerdict = 'probation' | 'passed' | 'failing' | 'not_applicable';

export interface PhoneNumberPerformance {
  id: string;
  number: string;
  status: string;
  probation: ProbationVerdict;
  probation_reason: string | null;
  region: string | null;
  dials: number;
  answered: number;
  answer_rate: number | null;
  short_calls: number;
  short_call_rate: number | null;
  enough_data: boolean;
  // The four components the SELECTOR scores on, plus their composite. NOT the same as
  // answer_rate / short_call_rate above: those are raw observations, these are
  // peer-shrunk — a number with little data reads close to its market's average rather
  // than its own noisy rate. null = never computed (the hourly job hasn't run yet).
  health_score: number | null;
  maturity: number | null;
  connection: number | null;
  engagement: number | null;
  sentiment: number | null;
  // Dials the components were computed from. 0 = they're peer priors, not measurements.
  health_sample: number;
  health_computed_at: string | null;
  hourly_calls: number;
  daily_calls: number;
  turn_cap: number;
  cost_micros: number;
}

export interface PhoneNumberPerformanceSummary {
  total: number;
  by_status: Record<string, number>;
  dials: number;
  answered: number;
  // The fleet baseline — an individual number is only meaningfully "bad" relative to
  // its peers dialing comparable lists, never against an absolute threshold.
  answer_rate: number | null;
  needs_attention: number;
}

export interface PhoneNumberPerformanceResponse {
  phone_numbers: PhoneNumberPerformance[];
  summary: PhoneNumberPerformanceSummary;
  window_days: number;
  min_sample: number;
  probation_days: number;
  // Lets the Short calls column say what 'short' means.
  short_call_seconds: number;
}

export const fetchPhoneNumberPerformance = (windowDays = 30) =>
  adminApi.get<PhoneNumberPerformanceResponse>(
    `/phone-numbers/performance?window_days=${windowDays}`,
  );

// Live caller-ID tuning. Every field here used to require a DEPLOY to change.
export interface CallerIdSettings {
  // Buying. Spends real money on a recurring basis, so it ships off (since 2026-09-03;
  // it shipped on before, but could never complete a purchase — ie1 has no search).
  auto_provision_enabled: boolean;
  auto_provision_max_per_run: number;
  auto_provision_max_per_day: number;
  auto_provision_max_total: number;
  auto_provision_countries: string[];
  // Dialing gates. Both change live behaviour, so both ship off.
  enforce_hourly_cap: boolean;
  use_health_selector: boolean;
  // Fair-share dials per number per UTC day, used by the hierarchy engine's
  // relaxation ladder. A target, not a limit — it is breached deliberately rather
  // than reach for a wrong-country number. `null` disables the preference.
  daily_soft_cap: number | null;
  // Absolute dials per number per UTC day — a reputation ceiling, not pacing. `null`
  // is no ceiling, which is how it ships; 0 is a ceiling of zero and stops dialing.
  // It is a FLEET-WIDE limit, so a value below (daily dials / fleet size) runs the
  // day out early.
  daily_hard_cap: number | null;
  // Which engine picks: legacy | weighted | hierarchy. The cutover switch and the
  // rollback. Not rendered — API-only until the hierarchy engine is proven.
  caller_id_engine: string;
  // Should the hierarchy engine step around numbers this lead has already been called
  // from? Not rendered, for the same reason as the engine above: it changes live
  // picking, so it is API-only until it has been watched on real traffic.
  rotate_recent_caller_ids: boolean;
  // Calling codes we refuse to dial, bare and without the `+` ("49"), because we hold no
  // local number there and a foreign caller ID kills pickup. Empty = dial everywhere;
  // buying a local number is what retires an entry. Optional because a core that predates
  // the column omits the key, and during a roll the browser talks to both versions.
  no_local_caller_id_calling_codes?: string[];
  // Selector weights. `region` is a hypothesis to measure, not a tuned value.
  weight_region: number;
  weight_health: number;
  weight_exploration: number;
  weight_wear: number;
  // Evidence thresholds.
  min_sample: number;
  probation_days: number;
  // The ONLY way call duration reaches the algorithm: answered-and-ended under this
  // = a "short call", which lowers health. Nothing reads the raw call length.
  short_call_seconds: number;
}

// What we bought, and how much headroom the caps leave.
//
// COUNTS, NOT DOLLARS — deliberately. Nothing fetches Twilio's per-number rental
// price (cost_micros elsewhere is per-CALL spend, a different bill), and an invented
// dollar figure would get budgeted against. Twilio's Pricing API is the honest fix.
export interface ProvisioningStats {
  bought_today: number;
  bought_7d: number;
  bought_30d: number;
  bought_total: number;
  total_numbers: number;
  by_status: Record<string, number>;
  recent: {
    id: number;
    number: string;
    region: string | null;
    status: string;
    purchased_at: string;
  }[];
  caps: {
    enabled: boolean;
    per_run: number;
    per_day: number;
    total: number;
    countries: string[];
  };
  // The two numbers that explain a quiet day — without them, "why did it stop
  // buying?" is a log dive.
  remaining_today: number;
  remaining_total: number;
}

export interface CallerIdSettingsResponse {
  settings: CallerIdSettings;
  // What the system ACTUALLY uses. Application env overrides the row, so these can
  // disagree — and if they do, editing the row changes nothing. Surfacing both is the
  // only way an operator can tell.
  effective: CallerIdSettings;
  stats: ProvisioningStats;
}

// =====================================================================
// Dial admission — the gates that decide whether a pool may place its next call.
// Read-only here on purpose: eleven knobs that can stop the fleet is not a form
// anyone should meet during an incident. Writes go through the API and the
// runbook (docs/dial-admission.md).
// =====================================================================
export interface DialerSettings {
  // The OUTCOME gates only. The mailbox gate below is armed by its own limit.
  triggers_enabled: boolean;
  wasted_stage1_pct: number;
  wasted_stage2_pct: number;
  placement_pct: number;
  window_seconds: number;
  min_samples: number;
  probe_interval_seconds: number;
  clean_probes_to_reopen: number;
  // null = the mailbox gate is off.
  mailbox_limit: number | null;
  backpressure_retry_ms: number;
  // Orders a pool's candidates by the LEAD's ring count, never-rung first, instead of by
  // drag position. The only knob here that changes which leads are dialled rather than
  // when — and it is not fairness-neutral across clients.
  ring_first_sort: boolean;
  // null = no bucketing. Rounds a lead's next ring UP to a boundary this many seconds
  // apart, so leads return in batches rather than smeared one at a time.
  ring_bucket_seconds: number | null;
  // Per-rung tolerance in seconds, indexed like the ladder. Empty = off. With this set
  // the effective floor becomes `rung - slack`, so the ladder stops being a floor.
  ring_slack_seconds: number[];
  // Anti-flap, both 0 = today's behaviour. A pool whose leads return within
  // `resting_horizon_seconds` is not treated as dark; `min_seat_seconds` floors how briefly
  // an SDR can be seated before the dark-eviction grace may fire.
  resting_horizon_seconds: number;
  min_seat_seconds: number;
  // Per-stream transport for the ClickHouse event writers (plans/event-queue):
  // absent key = direct (today's write path). Flips take effect on the next
  // event write with no deploy; {} is the full revert.
  event_transport: Partial<Record<EventStream, EventTransport>>;
  // The redial policy. null = the deploy-time default (what the fleet ran before any
  // of this was live); `DialerSettingsResponse.redial` says what is actually in force.
  // Seconds of rest after the 1st, 2nd, ... ring; the last entry repeats forever.
  ring_ladder: number[] | null;
  max_dials_per_lead: number | null;
  ring_release_max_rings: number | null;
  updated_at: string;
}

// The redial policy as the dial path resolves it: a stored value wins, else the deploy
// default. `deploy_ring_ladder` is shown so an operator can see what "default" means.
export interface RedialPolicy {
  ring_ladder: number[];
  deploy_ring_ladder: number[];
  max_dials_per_lead: number | null;
  ring_release_max_rings: number;
  // What a blank field falls back to.
  deploy_max_dials_per_lead: number | null;
  deploy_ring_release_max_rings: number;
}

// The ClickHouse event streams the core can route through the SQS event pipeline, in
// cutover order (boring first). The server owns the vocabulary, and a save sends the
// WHOLE map built from this list — so a name missing here is dropped from the stored
// map on the next save, and a name the server does not know 422s that save outright.
export const EVENT_STREAMS = [
  'sdr_pool_heartbeats',
  'call_stages',
  'pool_events',
  'call_events',
] as const;
export type EventStream = (typeof EVENT_STREAMS)[number];
export type EventTransport = 'direct' | 'queue';

export interface DialerSettingsResponse {
  settings: DialerSettings;
  // What the node serving THIS request is actually using: an env override beats the
  // row per node, and an operator seeing only the row could not tell. Covers the
  // dial-admission knobs only — event_transport's live view is the row itself
  // (Hermes.Config folds it per write, with no env override to diverge).
  effective: Omit<
    DialerSettings,
    | 'updated_at'
    | 'event_transport'
    | 'ring_ladder'
    | 'max_dials_per_lead'
    | 'ring_release_max_rings'
  >;
  redial: RedialPolicy;
  // pool_id -> the gate holding it. NODE-LOCAL: pools are spread across the fleet,
  // so this covers whichever node answered, not the cluster.
  tripped_pools: Record<string, string>;
}

export const fetchDialerSettings = (): Promise<DialerSettingsResponse> =>
  adminApi.get<DialerSettingsResponse>('/dialer-settings');

// The event-transport flip. Sends the WHOLE desired map — the server replaces
// the field wholesale, so every call states the full new world and {} is the
// panic revert (everything back to today's direct path). Only 'queue' entries
// are worth sending: an absent stream already reads as direct.
export const updateEventTransport = (
  transport: Partial<Record<EventStream, EventTransport>>,
): Promise<{ ok: boolean; settings: DialerSettings }> =>
  adminApi.patch<{ ok: true; settings: DialerSettings }>('/dialer-settings', {
    event_transport: transport,
  });

// Same world? Absent and 'direct' are one state (the reader treats any
// non-'queue' as direct), so only queue membership is compared. Backs the
// switch's stale-save guard: the PATCH replaces the map wholesale, so a Save
// must first prove the live map still matches the one its modal loaded.
export const transportMapsEqual = (
  a: Partial<Record<EventStream, EventTransport>> | undefined,
  b: Partial<Record<EventStream, EventTransport>> | undefined,
): boolean => EVENT_STREAMS.every((s) => (a?.[s] === 'queue') === (b?.[s] === 'queue'));

// The redial-policy edit. Any subset; null on a field returns it to the deploy default.
export const updateRedialPolicy = (patch: {
  ring_ladder?: number[] | null;
  max_dials_per_lead?: number | null;
  ring_release_max_rings?: number | null;
}): Promise<{ ok: boolean; settings: DialerSettings; redial: RedialPolicy }> =>
  adminApi.patch<{ ok: true; settings: DialerSettings; redial: RedialPolicy }>(
    '/dialer-settings',
    patch,
  );

// Hands the re-stamp to the background: every resting lead in every pool moves onto the
// ladder in force, forward-only. `expected` is the ladder the operator is looking at, and
// the server refuses (409) when the stored one differs — a forward-only write applied under
// a ladder nobody chose cannot be undone. 409 also means a run is already going.
export const restampRungs = (
  expected: number[],
): Promise<{ ok: boolean; started: boolean; ring_ladder: number[] }> =>
  adminApi.post<{ ok: true; started: true; ring_ladder: number[] }>(
    '/dialer-settings/restamp-rungs',
    { ring_ladder: expected },
  );

export const fetchCallerIdSettings = (): Promise<CallerIdSettingsResponse> =>
  adminApi.get<CallerIdSettingsResponse>('/caller-id-settings');

export const updateCallerIdSettings = (
  patch: Partial<CallerIdSettings>,
): Promise<{ ok: boolean; settings: CallerIdSettings; effective: CallerIdSettings }> =>
  adminApi.patch('/caller-id-settings', patch);

// One CANDIDATE recorded call in a pool (newest first). Derived from call_events on
// the core: every call that routed to an SDR (so Twilio started the bridged-leg
// recording). `sdr_id != null` proves the call bridged, NOT that a WAV was stored —
// sub-5s voicemail stubs are dropped and bridges can fail, so the viewer confirms
// real existence + the true length per row via `fetchRecordingStatuses`. `call_id`
// is exchanged for a playable presigned URL via `recordingUrl`; `at` is ISO-8601.
// `duration` here is the whole-CALL-leg duration (NOT the recording length) — the
// viewer shows the S3-sourced exact length instead.
export interface PoolRecording {
  call_id: string;
  sdr_id: number | null;
  contact_id: number;
  // The contact's name/phone at read time (null when the contact is gone or has
  // no name), so the viewer resolves the row's label without loading the whole
  // contacts table.
  contact_name: string | null;
  contact_phone: string | null;
  duration: number | null;
  at: string;
}

// Keyset cursor for recordings infinite scroll: the (at, call_id) of the last
// (oldest) row shown. Recordings group by call_id, so the tiebreaker is `call_id`
// (not the events' per-row `seq`).
export interface PoolRecordingsCursor {
  at: string;
  call_id: string;
}

// One page of a pool's candidate recordings, newest first. `limit` caps the page
// (core clamps to 1..1000, default 200); `before` keyset-pages to the next, older
// batch. A page shorter than `limit` means no more rows. `q` is an optional lead
// name/number substring, applied SERVER-SIDE against the FULL list (not just
// loaded pages) — appended only when non-empty.
export const fetchPoolRecordings = (
  id: number,
  opts: { limit?: number; before?: PoolRecordingsCursor; q?: string } = {},
): Promise<PoolRecording[]> => {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.before) {
    qs.set('before_at', opts.before.at);
    qs.set('before_call_id', opts.before.call_id);
  }
  const q = opts.q?.trim();
  if (q) qs.set('q', q);
  const suffix = qs.size ? `?${qs}` : '';
  return adminApi
    .get<{ recordings: PoolRecording[] }>(`/pools/${id}/recordings${suffix}`)
    .then((r) => r.recordings ?? []);
};

// Per-call recording existence + exact length, keyed by call_id. The core lists
// candidates (see PoolRecording); this resolves which actually have a stored WAV so
// the viewer hides the "—" rows, and returns each one's TRUE length (Twilio's
// RecordingDuration, whole seconds; null when unknown). Presigned by the api Lambda
// via the BFF at /bff/recordings/status (holds the API key; the browser never does).
// Same bearer-JWT auth as the admin calls. Empty input short-circuits (no request).
export interface RecordingStatus {
  exists: boolean;
  durationSec: number | null;
}
export async function fetchRecordingStatuses(
  callIds: string[],
): Promise<Record<string, RecordingStatus>> {
  const bodies = await postInBatches<{ callId: string } & RecordingStatus>(
    '/bff/recordings/status',
    callIds,
  );
  return Object.fromEntries(
    bodies.map((s) => [s.callId, { exists: s.exists, durationSec: s.durationSec }]),
  );
}

// --- Call history: cross-pool lead search -----------------------------------
// One bridged call. `outcome` is the core's own classification of how it ended;
// `talkMs` is the SDR's audio time and is null where the leg never carried any.
export interface CallHistoryCall {
  call_id: string;
  pool_id: number;
  pool_name: string | null;
  sdr_id: number | null;
  sdr_name: string | null;
  direction: string;
  talk_ms: number | null;
  outcome: string;
  at: string;
}

// One lead, with every bridged call we have for them, newest first.
export interface CallHistoryLead {
  contact_id: number;
  name: string | null;
  phone_number: string | null;
  email: string | null;
  timezone: string | null;
  dnc: boolean;
  account_name: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  client_name: string | null;
  throxy_client_id: string | null;
  last_call_at: string;
  calls: CallHistoryCall[];
}

// `truncated` = more leads matched than the cap returns; `row_capped` = the matched
// leads had more calls than the row cap. Both are shown rather than applied quietly: a
// short answer that looks complete is how someone concludes a call does not exist.
export interface CallHistoryPage {
  leads: CallHistoryLead[];
  truncated: boolean;
  row_capped: boolean;
}

export const fetchCallHistory = (q: string): Promise<CallHistoryPage> =>
  adminApi.get<CallHistoryPage>(`/call-history?q=${encodeURIComponent(q)}`);

// The api caps these batch reads at 200 call ids and answers 400 past it, so a caller
// with a longer page has to split. Splitting HERE rather than at each call site keeps the
// cap in one place, and keeps a view that outgrows it from failing outright.
const STATUS_BATCH = 200;

// Sequential, not `Promise.all`. These decorate a page, and the api Lambda they reach has
// a small reserved concurrency shared with every other caller — a wide page firing every
// batch at once would spend it on rows nobody is waiting for. Partial results beat none,
// so a failed batch ends the walk and returns what already arrived rather than discarding
// it: an undecorated row still renders.
async function postInBatches<T>(path: string, callIds: string[]): Promise<T[]> {
  if (callIds.length === 0) return [];
  const headers = { ...(await authHeader()), 'content-type': 'application/json' };
  const out: T[] = [];

  for (let i = 0; i < callIds.length; i += STATUS_BATCH) {
    const res = await fetch(path, {
      method: 'POST',
      headers,
      body: JSON.stringify({ callIds: callIds.slice(i, i + STATUS_BATCH) }),
    });
    if (!res.ok) {
      if (out.length > 0) return out;
      const text = await res.text();
      throw new AdminRequestError(
        res.status,
        (safeJson(text) as AdminError) || text || `request failed (${res.status})`,
      );
    }
    out.push(...((await res.json()) as { statuses: T[] }).statuses);
  }
  return out;
}

// A presigned GET URL for one call's recording WAV, fetched lazily when the
// operator plays a row. NOT under /bff/admin — recordings are presigned by the
// api Lambda, which the BFF bridges at /bff/recordings/* (it holds the API key;
// the browser never does). Same bearer-JWT auth as the admin calls.
// `intent` declares what the operator is about to do. Download gets its own short-lived
// URL and is recorded server-side; play is the default, so existing callers are unchanged
// and a BFF that predates this still answers them.
export async function recordingUrl(
  callId: string,
  intent: 'play' | 'download' = 'play',
): Promise<{ url: string; download: boolean }> {
  const headers = await authHeader();
  const suffix = intent === 'download' ? '?intent=download' : '';
  const res = await fetch(`/bff/recordings/calls/${encodeURIComponent(callId)}${suffix}`, {
    headers,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new AdminRequestError(
      res.status,
      (safeJson(text) as AdminError) || text || `request failed (${res.status})`,
    );
  }
  // `download` is absent from a BFF older than the intent it answers. Reading it as false
  // is what lets the caller tell "this was recorded and short-lived" from "this is the
  // ordinary playback URL", instead of claiming the first either way.
  const body = (await res.json()) as { url: string; download?: boolean };
  return { url: body.url, download: body.download ?? false };
}

// A call's transcript — the diarized conversation the jobs pipeline transcribed and
// wrote to S3, read back lazily when the operator opens a row's transcript (mirrors
// `recordingUrl`'s lazy fetch). NOT under /bff/admin — call artifacts are read by the
// api Lambda, which the BFF bridges at /bff/transcripts/* (it holds the API key; the
// browser never does). Same bearer-JWT auth as the admin calls. The envelope is the
// versioned artifact typed as `unknown` by the api contract; we read the fields the
// viewer renders. Throws AdminRequestError(404) when no transcript is stored yet.
export interface TranscriptSegment {
  speaker: string; // display label, e.g. "Speaker 1"
  startSec: number;
  endSec: number;
  text: string;
}
export interface Transcript {
  conversation: TranscriptSegment[];
  stats?: { speakerCount: number; chars: number };
}
export async function fetchTranscript(callId: string): Promise<Transcript> {
  const headers = await authHeader();
  const res = await fetch(`/bff/transcripts/calls/${encodeURIComponent(callId)}`, { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new AdminRequestError(
      res.status,
      (safeJson(text) as AdminError) || text || `request failed (${res.status})`,
    );
  }
  return (await res.json()) as Transcript;
}

// The latest outcome per call, keyed by call_id; absent when the classifier has not
// settled it. Batched through the BFF at /bff/dispositions/status. Empty input
// short-circuits.
export interface DispositionStatus {
  disposition: string;
  modelId: string;
  at: string;
}
export async function fetchDispositionStatuses(
  callIds: string[],
): Promise<Record<string, DispositionStatus>> {
  const bodies = await postInBatches<{ callId: string } & DispositionStatus>(
    '/bff/dispositions/status',
    callIds,
  );
  return Object.fromEntries(
    bodies.map((s) => [s.callId, { disposition: s.disposition, modelId: s.modelId, at: s.at }]),
  );
}

// One disposition in a pool's list — call id, human label, the full result JSON
// (`detail`), who answered, and when. Newest first, keyset-paginated. Bridged by the BFF
// at /bff/dispositions/pools/:id (holds the API key). Same bearer-JWT auth as the admin calls.
export interface PoolDisposition {
  callId: string;
  label: string;
  detail: string;
  answeredBy: string;
  modelId: string;
  at: string;
}
export interface PoolDispositionsPage {
  items: PoolDisposition[];
  nextCursor: string | null; // opaque; null on the last page
}
export async function fetchPoolDispositions(
  poolId: number,
  opts: { limit?: number; cursor?: string } = {},
): Promise<PoolDispositionsPage> {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.cursor) qs.set('cursor', opts.cursor);
  const suffix = qs.size ? `?${qs}` : '';
  const res = await fetch(`/bff/dispositions/pools/${poolId}${suffix}`, {
    headers: await authHeader(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new AdminRequestError(
      res.status,
      (safeJson(text) as AdminError) || text || `request failed (${res.status})`,
    );
  }
  return (await res.json()) as PoolDispositionsPage;
}

// --- Deploy status: which components are rolling right now (console banner) ---
// Mirrors apps/bff/src/clients/deploy-status.ts. NOT under /bff/admin (the BFF
// answers it directly from live AWS state, doesn't proxy to core); same bearer-JWT
// auth as the admin calls.
export type DeployComponentName = 'core' | 'bff' | 'api' | 'web';
export interface DeployComponentStatus {
  name: DeployComponentName;
  deploying: boolean;
  detail: string;
}
export interface DeployStatus {
  deploying: boolean;
  components: DeployComponentStatus[];
  checkedAt: string;
}

export async function fetchDeployStatus(): Promise<DeployStatus> {
  const headers = await authHeader();
  const res = await fetch('/bff/deploy-status', { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new AdminRequestError(
      res.status,
      (safeJson(text) as AdminError) || text || `request failed (${res.status})`,
    );
  }
  return (await res.json()) as DeployStatus;
}

export const setPoolHidden = (id: number, hidden: boolean): Promise<unknown> =>
  adminApi.post(`/pools/${id}/hidden`, { hidden });

// Open a pool to every SDR, or return it to its assigned roster. Closing sends home the
// SDRs who were only there because it was open.
export const setPoolOpenToAll = (id: number, open: boolean): Promise<unknown> =>
  adminApi.post(`/pools/${id}/open-to-all`, { open_to_all: open });

// The same, across every pool at once. `evicted` is how many SDRs a close sent back to
// the picker.
export const setAllPoolsOpenToAll = (
  open: boolean,
): Promise<{ changed: number[]; evicted: number }> =>
  adminApi.post<{ changed: number[]; evicted: number }>(`/pools/open-to-all`, {
    open_to_all: open,
  });

// Assign/unassign a person to a pool BY EMAIL — the console sources SDRs from the
// auth directory, so it links by the login email (the core get-or-creates the SDR
// row). This scopes that person's cockpit picker to the pool.
export const linkSdrByEmail = (poolId: number, email: string): Promise<unknown> =>
  adminApi.post(`/pools/${poolId}/sdrs/by-email`, { email });

export const unlinkSdrByEmail = (poolId: number, email: string): Promise<unknown> =>
  adminApi.del(`/pools/${poolId}/sdrs/by-email?email=${encodeURIComponent(email)}`);

// By-id assign/unassign — the DEV fallback path (auth off), where the roster comes
// from core SDR rows (some with no email, e.g. dev impersonation) that can only be
// keyed by their numeric id.
export const linkSdr = (id: number, poolId: number): Promise<unknown> =>
  adminApi.post(`/sdrs/${id}/pools/${poolId}`);

export const unlinkSdr = (id: number, poolId: number): Promise<unknown> =>
  adminApi.del(`/sdrs/${id}/pools/${poolId}`);

// `draining` is a seat that outlives the call still on it.
export const revokeSdrPools = (
  id: number,
): Promise<{
  ok: boolean;
  sdr: AdminSdr;
  revoked_pool_ids: number[];
  pushed_out: number;
  draining: number;
}> => adminApi.del(`/sdrs/${id}/pools`);

export const createContact = (name: string, phone: string, clientId: number): Promise<unknown> =>
  adminApi.post('/contacts', { name, phone, client_id: clientId });

// --- Bulk CSV import --------------------------------------------------------
// There's no core/BFF bulk endpoint yet, so we parse the CSV in the browser
// (csv-import.ts — pure, unit-tested) and fan out one POST /contacts per row
// (resolving each row's client NAME to its id, creating the client when it's
// new). The board re-fetches once at the end. Re-export the parser so the UI
// imports everything from one module.
export { parseCsv, rowsFromCsv } from './csv-import';
export type { CsvContactRow } from './csv-import';

// --- "Import throxy list" ---------------------------------------------------
// A richer, one-shot import: parse throxy lead exports in the browser
// (throxy-csv.ts — pure, unit-tested), merge them (one file per client — a pool
// can span several clients), then POST the whole list to the core's atomic
// /pools/import, which creates a brand-new pool and links every contact in
// merged order in ONE transaction. Re-export the parser so the UI imports
// everything from this one module (like the CSV import).
export { parseThroxyCsv, defaultListName, combineParsedFiles } from './throxy-csv';
export type {
  ThroxyContact,
  ThroxyImportContact,
  ThroxyMetadata,
  ThroxyParsedRow,
  ThroxyParseResult,
  FileWithClient,
  CombinedRow,
  CombinedImport,
} from './throxy-csv';

// Per-row failure from the core import (0-based index into the contacts sent).
export interface ThroxyImportFailure {
  index: number;
  name: string | null;
  error: string;
}

export interface ThroxyImportResult {
  // null when every row failed — the import is rolled back, so no pool is created.
  pool: { id: number; name: string } | null;
  // Distinct clients whose leads landed in the pool, in first-linked order.
  clients: { id: number; name: string }[];
  imported: number;
  // Contacts that already existed (same client + phone) and were LINKED to the new
  // pool instead of failing — only when reuseExisting was on.
  reused: number;
  // Numbers the core BOUGHT because the import's leads were in regions we owned no
  // number for. They serve every pool, not just this one — inventory is global.
  numbers_bought: number;
  failed: number;
  failures: ThroxyImportFailure[];
}

// One call: create the pool + contacts (with metadata) + dial-order links. Each
// contact carries its own `client_id` (from the file it came from); leads inherit
// that client's Throxy id so they sync to leadflow. `reuseExisting` links contacts
// that already exist (re-import) rather than failing on the per-client
// unique-phone constraint.
export async function importThroxyList(
  listName: string,
  contacts: ThroxyImportContact[],
  reuseExisting = false,
): Promise<ThroxyImportResult> {
  const res = await adminApi.post<{
    pool: { id: number; name: string } | null;
    clients?: { id: number; name: string }[];
    imported: number;
    reused?: number;
    numbers_bought?: number;
    failed: number;
    failures?: ThroxyImportFailure[];
  }>('/pools/import', {
    list_name: listName,
    contacts,
    reuse_existing: reuseExisting,
  });
  return {
    pool: res.pool,
    clients: res.clients ?? [],
    imported: res.imported,
    reused: res.reused ?? 0,
    numbers_bought: res.numbers_bought ?? 0,
    failed: res.failed,
    failures: res.failures ?? [],
  };
}

// Outcome for one input row — `ok` rows were created; failed rows carry why.
export interface BulkRowResult {
  line: number; // 1-based source line (incl. header) for "row N" messages
  fullName: string;
  phoneNumber: string;
  client: string;
  ok: boolean;
  error?: string;
}

export interface BulkImportSummary {
  imported: number;
  failed: number;
  clientsCreated: string[];
  results: BulkRowResult[];
}

// Import parsed rows: resolve each row's client name to an id (creating the
// client when it's new), then POST the contact. Sequential so per-row errors map
// cleanly and we never create the same new client twice. `existingClients` is the
// already-loaded board list; returns a per-row summary for the UI.
export async function importContactRows(
  rows: CsvContactRow[],
  existingClients: AdminClient[],
): Promise<BulkImportSummary> {
  // name (lowercased) -> id, seeded from the board and grown as we create clients.
  const clientIdByName = new Map<string, number>(
    existingClients.map((c) => [c.name.trim().toLowerCase(), c.id]),
  );
  const clientsCreated: string[] = [];
  const results: BulkRowResult[] = [];

  for (const [i, r] of rows.entries()) {
    const line = i + 2; // +1 for header, +1 for 1-based
    const base = { line, fullName: r.fullName, phoneNumber: r.phoneNumber, client: r.client };

    if (!r.fullName || !r.phoneNumber || !r.client) {
      const empty = [
        !r.fullName && 'full_name',
        !r.phoneNumber && 'phone_number',
        !r.client && 'client',
      ]
        .filter(Boolean)
        .join(', ');
      results.push({ ...base, ok: false, error: `Empty ${empty}` });
      continue;
    }

    try {
      const key = r.client.toLowerCase();
      let clientId = clientIdByName.get(key);
      if (clientId == null) {
        // Hidden path: creates a client by name with no Throxy id (so its leads
        // won't sync to leadflow). If this flow is ever restored, add a Throxy id.
        const created = (await adminApi.post<{ client: AdminClient }>('/clients', {
          name: r.client,
        })) as { client?: AdminClient };
        const newId = created?.client?.id;
        if (newId == null) throw new Error(`couldn't create client "${r.client}"`);
        clientId = newId;
        clientIdByName.set(key, newId);
        clientsCreated.push(r.client);
      }
      await createContact(r.fullName, r.phoneNumber, clientId);
      results.push({ ...base, ok: true });
    } catch (err) {
      const msg = err instanceof AdminRequestError ? err.message : String(err);
      results.push({ ...base, ok: false, error: msg });
    }
  }

  return {
    imported: results.filter((r) => r.ok).length,
    failed: results.filter((r) => !r.ok).length,
    clientsCreated,
    results,
  };
}

// The phrase the operator must type (and we send) to confirm a full data wipe.
// Must match the core's @wipe_confirmation. The core ALSO refuses in production.
export const WIPE_CONFIRMATION = 'DELETE ALL DATA';

// DESTRUCTIVE: wipe all dialer + event data (keeps auth/login). Irreversible.
// The core returns 403 in prod and 422 without the exact confirmation phrase.
export const wipeAllData = (): Promise<unknown> =>
  adminApi.post('/wipe', { confirm: WIPE_CONFIRMATION });

// A pool's lead counts by region bucket — the same keys the caller-ID grouping
// uses, so lead geography can be compared against the numbers we own.
export interface PoolLeadRegion {
  key: string;
  count: number;
}

// Which caller IDs a pool actually dialed with. A null human rate means no calls,
// which is distinct from a measured 0.

export type UsageWindow = '24h' | '7d' | '30d';

// One caller ID's activity within one pool: attributed to the pool, so the human
// rate is not smeared across every pool that dialed from the same number.
export interface PoolNumberRow {
  number: string;
  dials: number;
  calls: number;
  human_pct: number | null;
}

// Usage only — a number with no dials in the window is absent, not zeroed.
export interface PoolCallerIds {
  numbers: PoolNumberRow[];
}

export const fetchPoolCallerIds = (poolId: number, window: UsageWindow): Promise<PoolCallerIds> =>
  adminApi.get<PoolCallerIds>(`/pools/${poolId}/caller-ids?window=${window}`);

// HermesWeb.AdminController.sync_phone_numbers/2. `skipped` counts numbers already
// known, including soft-deleted ones (found, but never resurrected); `failed` counts
// entries Twilio owns that we could not store, e.g. an alphanumeric sender ID.
export interface PhoneNumberSyncResult {
  ok: boolean;
  fetched: number;
  created: number;
  skipped: number;
  failed: number;
}

// Idempotent, and it only LISTS Twilio inventory — buying is a separate route.
export const syncPhoneNumbersFromTwilio = (): Promise<PhoneNumberSyncResult> =>
  adminApi.post<PhoneNumberSyncResult>('/phone-numbers/sync');
